import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["customStrings", "disabled", "label", "onClear", "onBlur", "onSearchFieldBlur", "onKeyUp", "value"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { IconClear, IconSearch } from "../../";
import Button from "../../Button/Button";
import { mergeRefs } from "../../common/utilities";
import { FormContext } from "../../Form/FormContext";
import TextInput from "../../TextInput/TextInput";
import { visuallyHidden } from "../../themes/common/styles";
import Tooltip from "../Tooltip/Tooltip";
import extractTheme from "./extractTheme";
import { STRINGS } from "./strings";
/**
 * SearchField is a stylized version of a TextInput component that is designed to be
 * used in a searching context. It contains the magnifying glass icon to
 * denote '_Searching_' and a '_Clear_' button icon. It is to be used by
 * AutoComplete, FindBy and any other component that needs a search field.
 *
 * This private component uses the TextInput component,
 * therefore it accepts all of its props, such as `label`,
 * `placeholder`, `onChange`, `onFocus` and the rest.
 *
 * Clicking on the clear button does not clear the
 * field. It is left up to the parent component to handle that functionality.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const SearchField = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    customStrings,
    disabled,
    label,
    onClear,
    onBlur,
    onSearchFieldBlur,
    onKeyUp,
    // eslint-disable-line react/prop-types
    value
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    floatingLabel,
    cssCancelButton,
    cssSearchIcon,
    cssTextInput,
    cssTextInputLabel
  } = extractTheme({
    theme
  });
  const {
    resetDone
  } = useContext(FormContext) || {};
  const [noClearButton, setNoClearButton] = useState(!value);
  const inputRef = useRef("fromSearchField");
  /**
   * @description Handles the case where focus is moving from the input or
   * clear button to outside the SearchField component.
   * @param {EventFacade} e The event facade of the blur event.
   */

  const handleBlur = e => {
    var _inputRef$current$par;

    const {
      relatedTarget
    } = e;

    if (inputRef.current && !((_inputRef$current$par = inputRef.current.parentElement) !== null && _inputRef$current$par !== void 0 && _inputRef$current$par.contains(relatedTarget))) {
      onSearchFieldBlur && onSearchFieldBlur();
    }

    onBlur && onBlur(e);
  };
  /**
   * @description Executes the passed onClear function.
   * @param {EventFacade} e The event facade of the click event.
   */


  const handleClear = e => {
    setNoClearButton(true);
    inputRef.current.value = "";
    inputRef.current.focus();
    onClear && onClear(e);
  };
  /**
   * @description Executes the passed onKeyUp function.
   * @param {EventFacade} e The event facade of the click event.
   */


  const handleKeyUp = e => {
    const inputValue = e.target && e.target.value;
    setNoClearButton(!inputValue);
    onKeyUp && onKeyUp(e);
  };
  /**
   * Listens for changes in the TextInput value and shows the clear
   * button if there is a value. Useful for cases like Autocomplete where
   * a value is inserted into the TextInput without key presses.
   */


  useEffect(() => {
    if (inputRef.current.value.length > 0) {
      setNoClearButton(false);
    }
  }, [inputRef.current.value]);

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    requiredErrorMessage
  } = componentStrings;

  if (requiredErrorMessage) {
    let requiredMessage = null;
    typeof requiredErrorMessage === "function" ? requiredMessage = requiredErrorMessage(label) : requiredMessage = requiredErrorMessage;
    componentStrings.requiredErrorMessage = requiredMessage;
  } else {
    componentStrings.requiredErrorMessage = STRINGS.defaultRequiredErrorMessage(label);
  }

  useEffect(() => {
    if (resetDone) {
      setNoClearButton(!value);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [resetDone]);
  const {
    clearButtonAriaLabel,
    clearButtonLabel,
    searchText
  } = componentStrings;
  const clearButtonLabelValue = clearButtonLabel(label);
  const clearButtonAriaLabelValue = clearButtonAriaLabel(label);
  const actualLabel = floatingLabel ? _jsxs(React.Fragment, {
    children: [_jsx(IconSearch, {
      size: "small",
      display: "inline-block",
      spacing: {
        r: 1
      }
    }), label]
  }) : _jsxs(React.Fragment, {
    children: [_jsx("span", {
      css: visuallyHidden,
      children: searchText
    }), label]
  });
  const actualRightElement = floatingLabel ? _jsx(Button, {
    "aria-label": clearButtonAriaLabelValue,
    centerIcon: _jsx(IconClear, {
      size: "small"
    }),
    css: cssCancelButton,
    disabled: disabled,
    kind: "simple",
    label: clearButtonLabelValue,
    onClick: handleClear,
    onBlur: handleBlur
  }) : _jsx(Tooltip, {
    label: clearButtonLabelValue,
    trigger: _jsx(Button, {
      "aria-label": clearButtonAriaLabelValue,
      centerIcon: _jsx(IconClear, {
        size: "xsmall"
      }),
      css: cssCancelButton,
      disabled: disabled,
      kind: "simple",
      onBlur: handleBlur,
      onClick: handleClear
    }),
    children: clearButtonLabelValue
  });
  return _jsx(TextInput, _objectSpread({
    label: actualLabel,
    ref: Array.isArray(userRef) ? mergeRefs([...userRef, inputRef]) : mergeRefs([userRef, inputRef]),
    disabled: disabled,
    value: value,
    onBlur: handleBlur,
    onKeyUp: handleKeyUp,
    textInputCSS: cssTextInput,
    textInputLabelCSS: cssTextInputLabel,
    customStrings: componentStrings,
    leftElement: floatingLabel ? null : _jsx("div", {
      css: cssSearchIcon,
      children: _jsx(IconSearch, {
        size: "xsmall",
        disabled: disabled
      })
    }),
    rightElement: !noClearButton ? actualRightElement : null
  }, otherProps));
});
SearchField.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Label of the Field. */
  label: PropTypes.string.isRequired,

  /* Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * The aria label of the Clear button.
     * Default: '(label) => `Clear ${label}`'
     */
    clearButtonAriaLabel: PropTypes.func,

    /**
     * The label of the Clear button.
     * Default: '(label) => `Clear ${label}`'
     */
    clearButtonLabel: PropTypes.func,
    requiredErrorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
  }),

  /** Disable or enable the field. */
  disabled: PropTypes.bool,

  /** A function to be executed after the clear button is clicked. */
  onClear: PropTypes.func,

  /**
   * Handler for the blur event on the component. Will trigger when focus is not on
   * any part of the component. This includes the TextInput and Button.
   */
  onSearchFieldBlur: PropTypes.func,

  /** Field value. */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
} : {};
SearchField.defaultProps = {
  customStrings: {},
  disabled: false,
  onClear: null,
  onSearchFieldBlur: null,
  value: null
};
SearchField.displayName = "SearchField";
export default SearchField;